import { z } from 'zod';
import type {
  ResourceTypeFieldAssignment,
  ResourceTypeWorkspaceBinding
} from './resource-type-bindings.api';

const fieldAssignmentBase$ = z.object({
  id: z.string(),
  required: z.boolean().optional().default(false),
  locked: z.boolean().optional().default(false),
  order: z.number().optional().default(0),
  /** Prefer `active`; migrate legacy `archived` when present. */
  active: z.boolean().optional(),
  archived: z.boolean().optional()
});

const fieldAssignment$ = z
  .union([
    fieldAssignmentBase$.extend({
      source: z.literal('core'),
      coreFieldKey: z.string()
    }),
    fieldAssignmentBase$.extend({
      source: z.literal('custom'),
      customFieldDefinitionId: z.number()
    })
  ])
  .transform((field): ResourceTypeFieldAssignment => {
    const active =
      field.active ?? (field.archived != null ? !field.archived : true);
    if (field.source === 'core') {
      return {
        id: field.id,
        source: 'core',
        coreFieldKey: field.coreFieldKey,
        required: field.required,
        locked: field.locked,
        active,
        order: field.order
      };
    }
    return {
      id: field.id,
      source: 'custom',
      customFieldDefinitionId: field.customFieldDefinitionId,
      required: field.required,
      locked: field.locked,
      active,
      order: field.order
    };
  });

const binding$ = z
  .object({
    resourceTypeId: z.number(),
    workspaceId: z.number(),
    enabled: z.boolean(),
    workspaceFields: z.array(fieldAssignment$).optional().default([])
  })
  .transform(
    (binding): ResourceTypeWorkspaceBinding => ({
      resourceTypeId: binding.resourceTypeId,
      workspaceId: binding.workspaceId,
      enabled: binding.enabled,
      workspaceFields: binding.workspaceFields
    })
  );

/**
 * localStorage payload for resource-type workspace bindings (interim until HTTP).
 * Zod `.catch` keeps a corrupt store from breaking the app (same idea as
 * `global-search.api.ls.schema.ts`).
 */
export const resourceTypeBindingsStore$ = z
  .object({
    byOrganization: z
      .record(z.string(), z.array(binding$))
      .optional()
      .default({})
  })
  .catch(e => {
    console.warn('Error parsing resourceTypeBindingsStore$', e);
    return { byOrganization: {} };
  });

export type ResourceTypeBindingsStore = z.infer<
  typeof resourceTypeBindingsStore$
>;

export const RESOURCE_TYPE_BINDINGS_STORAGE_KEY =
  'resource-types-admin.bindings.v1';

export const EMPTY_RESOURCE_TYPE_BINDINGS_STORE: ResourceTypeBindingsStore = {
  byOrganization: {}
};
